import { createError, formatDate, getLastDateStringOfMonthYear } from '@common/neo.tax';
import isNil from 'lodash/isNil';

import {
  BooleanFieldComparison,
  DateFieldComparison,
  FloatFieldComparison,
  InputMaybe,
  StringFieldComparison,
} from '@neo.tax/shared/graphql';
import { Conditions, OperatorName } from '@neo.tax/shared/organisms';

import { FilterSortDataType } from './enums';

/**
 * A field which the given `nestjs-query` filter can filter by. If `ValueType`
 * is given, only fields of that type. Currently does not support boolean
 * fields, which use `is` instead of `eq`.
 *
 * @see https://doug-martin.github.io/nestjs-query/docs/concepts/queries
 */
export type FilterField<Filter, ValueType = any> = Extract<
  keyof {
    [
      K in keyof Filter as Exclude<Filter[K], null | undefined> extends { eq?: (infer Eq) | null }
        ? // Make sure ValueType and the type in the filter extend each other, meaning
          // they're exactly the same type. This means, for instance, that `string`
          // will find only actual string fields, and not enums.
          [ValueType, Eq] extends [Eq, ValueType]
          ? K
          : never
        : Exclude<Filter[K], null | undefined> extends { is?: (infer Is) | null }
          ? [ValueType, Is] extends [Is, ValueType]
            ? K
            : never
          : never
    ]: true;
  },
  string & keyof Filter
>;

const escapeWildcards = (text: string): string => text.replace(/%/g, '\\%');

/**
 * Returns an {@link OtherStuff} which filters for the keywords in
 * {@link searchKeyword} appearing in any of the given {@link fields}. The
 * filter will match any record which contains *all* of the keywords in the
 * string, case-insensitively, across *any* of the fields.
 */
export const keywordFilter = <Filter>(
  fields: FilterField<Filter, string>[],
  searchKeyword: string | null | undefined,
): { and: { or: Filter[] }[] } => ({
  and: !searchKeyword
    ? []
    : searchKeyword.split(/\s+/).map((keyword): { or: Filter[] } => ({
        or: fields.map(
          (field): Filter =>
            ({
              [field]: { iLike: `%${escapeWildcards(keyword)}%` },
            }) as Filter,
        ),
      })),
});

export class BadConditionValueError extends Error {
  constructor(
    readonly operator: OperatorName,
    readonly value: string | number,
    expectedType: string,
  ) {
    super(`Tried to compare with ${operator} to ${JSON.stringify(value)}; should have been ${expectedType}`);
  }
}

// "Assertions require every name in the call target to be declared with an
// explicit type annotation." - ts(2775)
const assertStringValue: (operator: OperatorName, value: string | number) => asserts value is string = (
  operator,
  value,
) => {
  if (typeof value !== 'string') {
    throw new BadConditionValueError(operator, value, 'string');
  }
};

const assertNumberValue: (operator: OperatorName, value: string | number) => asserts value is number = (
  operator,
  value,
) => {
  if (typeof value !== 'number') {
    throw new BadConditionValueError(operator, value, 'number');
  }
};

const makeStringComparison = (operator: OperatorName, value: string): StringFieldComparison => {
  switch (operator) {
    case OperatorName.IS:
      return { eq: value };
    case OperatorName.IS_NOT:
      return { neq: value };
    case OperatorName.STARTS_WITH:
      return { iLike: `${escapeWildcards(value)}%` };
    case OperatorName.ENDS_WITH:
      return { iLike: `%${escapeWildcards(value)}` };
    case OperatorName.CONTAINS:
      return { iLike: `%${escapeWildcards(value)}%` };
    case OperatorName.DOES_NOT_CONTAIN:
      return { notILike: `%${escapeWildcards(value)}%` };
    default:
      throw createError(
        'BadComparisonOperator',
        `Tried to use ${operator} to compare a string (${JSON.stringify(value)})`,
      );
  }
};

const makeNumberComparison = (operator: OperatorName, value: number): FloatFieldComparison => {
  switch (operator) {
    case OperatorName.IS:
      return { eq: value };
    case OperatorName.IS_NOT:
      return { neq: value };
    case OperatorName.GREATER_THAN:
      return { gt: value };
    case OperatorName.GREATER_THAN_OR_EQUAL_TO:
      return { gte: value };
    case OperatorName.LESS_THAN:
      return { lt: value };
    case OperatorName.LESS_THAN_OR_EQUAL_TO:
      return { lte: value };
    default:
      throw createError(
        'BadComparisonOperator',
        `Tried to use ${operator} to compare a number (${JSON.stringify(value)})`,
      );
  }
};

const makeDateComparison = (operator: OperatorName, value: string): DateFieldComparison => {
  // Ensure value is a valid date string. This should match the DateValueInput mask.
  const DATE_INPUT_REGEX = /^(0[1-9]|1[012])\/(0[1-9]|[12][0-9]|3[01])\/(19|20)\d{2}$/; // mm/dd/yyyy
  if (!DATE_INPUT_REGEX.test(value)) {
    throw createError(
      'InvalidDateComparisonValue',
      `Tried to use ${operator} to compare an invalid date (${JSON.stringify(value)})`,
    );
  }

  const dateString = formatDate(value);
  switch (operator) {
    case OperatorName.IS:
      return { eq: dateString };
    case OperatorName.IS_NOT:
      return { neq: dateString };
    case OperatorName.GREATER_THAN:
      return { gt: dateString };
    case OperatorName.GREATER_THAN_OR_EQUAL_TO:
      return { gte: dateString };
    case OperatorName.LESS_THAN:
      return { lt: dateString };
    case OperatorName.LESS_THAN_OR_EQUAL_TO:
      return { lte: dateString };
    default:
      throw createError(
        'BadComparisonOperator',
        `Tried to use ${operator} to compare a date (${JSON.stringify(value)})`,
      );
  }
};

const makeMonthYearStartComparison = (operator: OperatorName, value: string): DateFieldComparison => {
  // Ensure value is a valid date string. This should match the DateValueInput mask.
  const MONTH_YEAR_STRING_REGEX = /^(19|20)\d{2}-(0[1-9]|1[012])$/; // yyyy-mm
  if (!MONTH_YEAR_STRING_REGEX.test(value)) {
    throw createError(
      'InvalidMonthYearComparisonValue',
      `Tried to use ${operator} to compare an invalid month-year string (${JSON.stringify(value)})`,
    );
  }

  const dateString = `${value}-01`;
  switch (operator) {
    case OperatorName.IS:
      return { eq: dateString };
    case OperatorName.IS_NOT:
      return { neq: dateString };
    case OperatorName.GREATER_THAN:
      return { gt: dateString };
    case OperatorName.GREATER_THAN_OR_EQUAL_TO:
      return { gte: dateString };
    case OperatorName.LESS_THAN:
      return { lt: dateString };
    case OperatorName.LESS_THAN_OR_EQUAL_TO:
      return { lte: dateString };
    default:
      throw createError(
        'BadComparisonOperator',
        `Tried to use ${operator} to compare a month-year start (${JSON.stringify(value)})`,
      );
  }
};

const makeMonthYearEndComparison = (operator: OperatorName, value: string): DateFieldComparison => {
  // Ensure value is a valid date string. This should match the DateValueInput mask.
  const MONTH_YEAR_STRING_REGEX = /^(19|20)\d{2}-(0[1-9]|1[012])$/; // yyyy-mm
  if (!MONTH_YEAR_STRING_REGEX.test(value)) {
    throw createError(
      'InvalidMonthYearComparisonValue',
      `Tried to use ${operator} to compare an invalid month-year string (${JSON.stringify(value)})`,
    );
  }

  const dateString = getLastDateStringOfMonthYear(value);
  switch (operator) {
    case OperatorName.IS:
      return { eq: dateString };
    case OperatorName.IS_NOT:
      return { neq: dateString };
    case OperatorName.GREATER_THAN:
      return { gt: dateString };
    case OperatorName.GREATER_THAN_OR_EQUAL_TO:
      return { gte: dateString };
    case OperatorName.LESS_THAN:
      return { lt: dateString };
    case OperatorName.LESS_THAN_OR_EQUAL_TO:
      return { lte: dateString };
    default:
      throw createError(
        'BadComparisonOperator',
        `Tried to use ${operator} to compare a month-year end (${JSON.stringify(value)})`,
      );
  }
};

const makeBooleanComparison = (operator: OperatorName, value: string): BooleanFieldComparison => {
  if (value !== 'true' && value !== 'false') {
    throw createError('BadComparisonValue', `Tried to use ${operator} to compare a boolean (${JSON.stringify(value)})`);
  }
  const matched = value === 'true';
  switch (operator) {
    case OperatorName.IS:
      return { is: matched };
    case OperatorName.IS_NOT:
      return { isNot: matched };
    default:
      throw createError(
        'BadComparisonOperator',
        `Tried to use ${operator} to compare a boolean (${JSON.stringify(value)})`,
      );
  }
};

const makeValueComparison = (
  dataType: FilterSortDataType,
  operator: OperatorName,
  value: string | number,
): BooleanFieldComparison | DateFieldComparison | FloatFieldComparison | StringFieldComparison => {
  switch (dataType) {
    case FilterSortDataType.NUMBER:
      assertNumberValue(operator, value);
      return makeNumberComparison(operator, value);
    case FilterSortDataType.DATE:
      assertStringValue(operator, value);
      return makeDateComparison(operator, value);
    case FilterSortDataType.MONTH_YEAR_START:
      assertStringValue(operator, value);
      return makeMonthYearStartComparison(operator, value);
    case FilterSortDataType.MONTH_YEAR_END:
      assertStringValue(operator, value);
      return makeMonthYearEndComparison(operator, value);
    case FilterSortDataType.BOOLEAN:
      assertStringValue(operator, value);
      return makeBooleanComparison(operator, value);
    default:
      assertStringValue(operator, value);
      return makeStringComparison(operator, value);
  }
};

/**
 * Returns an {@link Filter} which filters for the given conditions.
 */
export const advancedFilter = <
  Filter extends {
    and?: InputMaybe<Array<Pick<Filter, FilterField<Filter>>>>;
    or?: InputMaybe<Array<Pick<Filter, FilterField<Filter>>>>;
  },
>(
  conditions: Conditions<FilterField<Filter>> | undefined,
): { and: Pick<Filter, FilterField<Filter>>[] } | { or: Pick<Filter, FilterField<Filter>>[] } => {
  // If `conditions` is not present, noop filter.
  if (!conditions) {
    return { and: [] };
  }

  const subFilters = conditions.conditionRows.flatMap(
    ({ column, dataType, operator, value }): Pick<Filter, FilterField<Filter>>[] => {
      if (isNil(value)) {
        return [];
      }

      try {
        return [
          {
            [column]: makeValueComparison(dataType, operator, value),
          } as Pick<Filter, FilterField<Filter>>,
        ];
      } catch (error) {
        // This is most likely because the user hasn't finished typing the date so just ignore it.
        if (error instanceof Error && error.name === 'InvalidDateComparisonValue') {
          return [];
        }
        throw error;
      }
    },
  );

  if (conditions.booleanOperator === 'OR') {
    return { or: subFilters };
  } else {
    return { and: subFilters };
  }
};
